/* Market cart guard — removes cart lines the current market does not sell.
 *
 * Shopify keeps cart lines across a region switch and only reprices them, even
 * when the new market's catalog excludes the product or the variant. Config
 * comes from snippets/market-cart-guard.liquid ([data-market-cart-guard]).
 *
 *   1. Runs a check only when the market differs from the last checked one
 *      (localStorage), so normal page views cost no requests.
 *   2. Reads /cart.js and the market-filtered product JSON of every cart
 *      product (via the line's localized URL). A 404 means the product is not
 *      in the market's catalog; a missing variant id means the variant is not.
 *   3. Removes those lines and configurator add-ons whose parent is removed,
 *      then reloads the cart page or refreshes the cart drawer.
 *   4. Shows a dismissible notice in every .custom-cart-panel (cart page and
 *      drawer) and re-inserts it after the drawer re-renders.
 *
 * Network or server errors never remove anything; the check is retried on
 * the next page view.
 */
(function () {
  if (window.__aerofoilsMarketCartGuardInit) return;
  window.__aerofoilsMarketCartGuardInit = true;

  var CHECKED_KEY = "aerofoils:market-cart:checked";
  var NOTICE_KEY = "aerofoils:market-cart:removed";
  var PANEL_SELECTOR = ".custom-cart-panel";
  var NOTICE_CLASS = "custom-cart-market-notice";

  var configElement = document.querySelector("[data-market-cart-guard]");
  if (!configElement) return;

  var config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  var root = String(config.root || "/").replace(/\/$/, "");
  var country = String(config.country || "").toUpperCase();
  var labels = config.labels || {};

  function storageGet(type, key) {
    try {
      return window[type].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(type, key, value) {
    try {
      window[type].setItem(key, value);
    } catch (error) {
      /* storage blocked: the check simply runs again next time */
    }
  }

  function storageRemove(type, key) {
    try {
      window[type].removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }

  function fetchJson(url) {
    return fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  }

  /* ------------------------------------------------------------------ *
   * 1.–3. Check and remove
   * ------------------------------------------------------------------ */

  function productJsonUrl(item) {
    var path = String(item.url || "").split("?")[0];
    return path ? path + ".js" : "";
  }

  // Resolves to { url: Set<variantId> | null } — null when the product is not
  // sold in this market. Rejects on anything that is not a clear answer.
  function loadMarketVariants(items) {
    var urls = [];
    items.forEach(function (item) {
      var url = productJsonUrl(item);
      if (url && urls.indexOf(url) === -1) urls.push(url);
    });

    return Promise.all(
      urls.map(function (url) {
        return fetchJson(url).then(function (response) {
          if (response.status === 404) return [url, null];
          if (!response.ok) throw new Error("Product lookup failed: " + response.status);
          return response.json().then(function (product) {
            var ids = new Set();
            (product.variants || []).forEach(function (variant) {
              ids.add(String(variant.id));
            });
            return [url, ids];
          });
        });
      }),
    ).then(function (entries) {
      var map = {};
      entries.forEach(function (entry) {
        map[entry[0]] = entry[1];
      });
      return map;
    });
  }

  function addonParentId(item) {
    var properties = item.properties || {};
    return String(
      properties._aerofoils_addon_parent ||
        properties._bundle_addon_parent_variant_id ||
        "",
    );
  }

  function isHiddenLine(item) {
    var title = String(item.product_title || item.title || "").toLowerCase();
    return title.indexOf("(parent)") !== -1 && Number(item.final_line_price || 0) === 0;
  }

  function findUnavailableLines(cart, marketVariants) {
    var removed = [];
    var removedVariantIds = [];

    cart.items.forEach(function (item) {
      var ids = marketVariants[productJsonUrl(item)];
      if (ids === undefined) return;
      if (ids === null || !ids.has(String(item.variant_id))) {
        removed.push(item);
        removedVariantIds.push(String(item.variant_id));
      }
    });

    var listed = removed.slice();

    cart.items.forEach(function (item) {
      var parentId = addonParentId(item);
      if (parentId && removedVariantIds.indexOf(parentId) !== -1 && removed.indexOf(item) === -1) {
        removed.push(item);
      }
    });

    // `listed` excludes add-ons that only go because their parent goes.
    return { lines: removed, listed: listed };
  }

  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (item) {
      updates[item.key] = 0;
    });

    return fetch(root + "/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates: updates }),
    }).then(function (response) {
      if (!response.ok) throw new Error("Cart update failed: " + response.status);
      return response.json();
    });
  }

  function afterRemoval() {
    if (document.querySelector(".custom-cart-page")) {
      window.location.reload();
      return;
    }

    if (window.AerofoilsCartUI && typeof window.AerofoilsCartUI.refresh === "function") {
      window.AerofoilsCartUI.refresh({}).catch(function () {});
    }
  }

  function markChecked() {
    storageSet("localStorage", CHECKED_KEY, country);
  }

  function runCheck() {
    if (!country) return;
    if (Number(config.itemCount || 0) === 0) {
      markChecked();
      return;
    }
    if (storageGet("localStorage", CHECKED_KEY) === country) return;

    fetchJson(root + "/cart.js")
      .then(function (response) {
        if (!response.ok) throw new Error("Cart lookup failed: " + response.status);
        return response.json();
      })
      .then(function (cart) {
        var items = (cart.items || []).filter(function (item) {
          return Number(item.quantity || 0) > 0;
        });
        if (!items.length) return null;

        return loadMarketVariants(items).then(function (marketVariants) {
          var result = findUnavailableLines({ items: items }, marketVariants);
          if (!result.lines.length) return null;

          return removeLines(result.lines).then(function () {
            return result.listed;
          });
        });
      })
      .then(function (removedLines) {
        markChecked();
        if (!removedLines) return;

        var titles = removedLines
          .filter(function (item) {
            return !isHiddenLine(item);
          })
          .map(function (item) {
            return String(item.title || item.product_title || "");
          })
          .filter(Boolean);

        storageSet(
          "sessionStorage",
          NOTICE_KEY,
          JSON.stringify({ country: country, titles: titles }),
        );
        renderNotices();
        afterRemoval();
      })
      .catch(function (error) {
        console.warn("Market cart guard:", error);
      });
  }

  /* ------------------------------------------------------------------ *
   * 4. Notice
   * ------------------------------------------------------------------ */

  var observedPanels = typeof WeakSet === "function" ? new WeakSet() : null;

  function readNotice() {
    var raw = storageGet("sessionStorage", NOTICE_KEY);
    if (!raw) return null;
    try {
      var notice = JSON.parse(raw);
      // A notice from another market (region switched again) is outdated.
      return notice && notice.country === country ? notice : null;
    } catch (error) {
      return null;
    }
  }

  function dismissNotice() {
    storageRemove("sessionStorage", NOTICE_KEY);
    Array.prototype.slice
      .call(document.querySelectorAll("." + NOTICE_CLASS))
      .forEach(function (element) {
        element.remove();
      });
  }

  function buildNotice(notice) {
    var element = document.createElement("div");
    element.className = NOTICE_CLASS;
    element.setAttribute("role", "status");

    var body = document.createElement("div");
    body.className = NOTICE_CLASS + "__body";

    var title = document.createElement("p");
    title.className = NOTICE_CLASS + "__title";
    title.textContent = labels.title || "";
    body.appendChild(title);

    if (notice.titles && notice.titles.length) {
      var text = document.createElement("p");
      text.className = NOTICE_CLASS + "__text";
      text.textContent = labels.text || "";
      body.appendChild(text);

      var list = document.createElement("ul");
      list.className = NOTICE_CLASS + "__list";
      notice.titles.forEach(function (itemTitle) {
        var entry = document.createElement("li");
        entry.textContent = itemTitle;
        list.appendChild(entry);
      });
      body.appendChild(list);
    }

    var close = document.createElement("button");
    close.type = "button";
    close.className = NOTICE_CLASS + "__close";
    close.setAttribute("aria-label", labels.dismiss || "");
    close.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    close.addEventListener("click", dismissNotice);

    element.appendChild(body);
    element.appendChild(close);
    return element;
  }

  function insertNotice(panel, notice) {
    if (panel.querySelector("." + NOTICE_CLASS)) return;
    var element = buildNotice(notice);
    var header = panel.querySelector(".custom-cart-header");
    if (header && header.parentNode === panel) {
      header.insertAdjacentElement("afterend", element);
    } else {
      panel.insertBefore(element, panel.firstChild);
    }
  }

  function renderNotices() {
    var notice = readNotice();
    if (!notice) return;

    Array.prototype.slice.call(document.querySelectorAll(PANEL_SELECTOR)).forEach(function (panel) {
      insertNotice(panel, notice);

      // The drawer replaces its innerHTML on every refresh.
      if (observedPanels && !observedPanels.has(panel) && typeof MutationObserver === "function") {
        observedPanels.add(panel);
        new MutationObserver(function () {
          var current = readNotice();
          if (current) insertNotice(panel, current);
        }).observe(panel, { childList: true });
      }
    });
  }

  function init() {
    renderNotices();
    runCheck();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
